import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type { Message, MessageDraftMode, ResumableDraftProjection } from '@vivport/backend/types';
import { MessageDraftRevisionNotice } from '@/features/messaging/MessageDraftRevisionNotice';
import type { MessageDraftRevision } from '@/features/messaging/useMessageDraftLifecycle';
import {
  ChevronDown,
  Ellipsis,
  HardDrive,
  Image as ImageIcon,
  Link2,
  Loader2,
  MoreVertical,
  Paperclip,
  Smile,
  Trash2,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

const ATTACHMENT_TOTAL_BYTES_LIMIT = 25 * 1024 * 1024;

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export type ComposerDraftState = {
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  authoredBody: string;
  quotedBody: string;
};

export const EMPTY_COMPOSER_DRAFT: ComposerDraftState = {
  to: '',
  cc: '',
  bcc: '',
  subject: '',
  authoredBody: '',
  quotedBody: '',
};

export type MessageInlineReplyComposerProps = {
  sourceMessage: Message;
  mode: MessageDraftMode;
  draft: ComposerDraftState;
  attachments: File[];
  onModeChange: (mode: MessageDraftMode) => void;
  onDraftChange: (next: ComposerDraftState) => void;
  onAttachmentsChange: (next: File[]) => void;
  onSend: () => void;
  onCancel: () => void;
  isSending: boolean;
  revision: MessageDraftRevision;
};

const MODE_LABEL: Record<MessageDraftMode, string> = {
  reply: 'Reply',
  replyAll: 'Reply all',
  forward: 'Forward',
  newFromContext: 'New',
};

const PRIMARY_FOOTER_ICONS: Array<{ Icon: typeof Link2; label: string }> = [
  { Icon: Link2, label: 'Insert link' },
  { Icon: Smile, label: 'Insert emoji' },
  { Icon: HardDrive, label: 'Insert from Drive' },
  { Icon: ImageIcon, label: 'Insert image' },
];

export function buildComposerPrefillFromDefaults(
  message: Message,
  mode: MessageDraftMode
): ComposerDraftState {
  const defaults = message.messageDraftDefaults?.byMode?.[mode];
  if (!defaults) {
    return { ...EMPTY_COMPOSER_DRAFT };
  }
  const toNormalized = defaults.to
    .map((email) => email.trim().toLowerCase())
    .filter((email) => email.length > 0);
  const toSet = new Set(toNormalized);
  const ccNormalized = defaults.cc
    .map((email) => email.trim().toLowerCase())
    .filter((email) => email.length > 0 && !toSet.has(email));
  const bccNormalized = defaults.bcc
    .map((email) => email.trim().toLowerCase())
    .filter((email) => email.length > 0);
  return {
    to: toNormalized.join(', '),
    cc: ccNormalized.join(', '),
    bcc: bccNormalized.join(', '),
    subject: defaults.subject,
    authoredBody: '',
    quotedBody: defaults.body.replace(/^\s+/, ''),
  };
}

/**
 * Prefill the composer from a persisted resumable draft (its saved authored body +
 * recipients), reusing the per-mode scaffold only for the collapsed quoted trailer.
 */
export function buildComposerPrefillFromResumable(
  message: Message,
  resumable: ResumableDraftProjection
): ComposerDraftState {
  const base = buildComposerPrefillFromDefaults(message, resumable.sourceMode);
  const toNormalized = resumable.to
    .map((email) => email.trim().toLowerCase())
    .filter((email) => email.length > 0);
  const toSet = new Set(toNormalized);
  const ccNormalized = resumable.cc
    .map((email) => email.trim().toLowerCase())
    .filter((email) => email.length > 0 && !toSet.has(email));
  const bccNormalized = resumable.bcc
    .map((email) => email.trim().toLowerCase())
    .filter((email) => email.length > 0);
  return {
    to: toNormalized.join(', '),
    cc: ccNormalized.join(', '),
    bcc: bccNormalized.join(', '),
    subject: resumable.subject,
    authoredBody: resumable.authoredBody,
    quotedBody: base.quotedBody,
  };
}

function buildRecipientPillText(message: Message, mode: MessageDraftMode): string {
  const defaults = message.messageDraftDefaults?.byMode?.[mode];
  if (!defaults || defaults.to.length === 0) {
    return 'to (no recipients)';
  }
  const items = message.conversation?.items ?? [];
  const nameByEmail = new Map<string, string>();
  for (const item of items) {
    const fromAddress = item.from;
    if (fromAddress?.email) {
      nameByEmail.set(
        fromAddress.email.toLowerCase(),
        fromAddress.name?.trim() || fromAddress.email
      );
    }
    for (const addr of [...item.to, ...item.cc, ...item.bcc]) {
      if (addr?.email) {
        nameByEmail.set(addr.email.toLowerCase(), addr.name?.trim() || addr.email);
      }
    }
  }
  const display = defaults.to.map((email) => nameByEmail.get(email.toLowerCase()) ?? email);
  return `to ${display.join(', ')}`;
}

export function MessageInlineReplyComposer({
  sourceMessage,
  mode,
  draft,
  attachments,
  onModeChange,
  onDraftChange,
  onAttachmentsChange,
  onSend,
  onCancel,
  isSending,
  revision,
}: MessageInlineReplyComposerProps) {
  const bodyRef = useRef<HTMLTextAreaElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [headersExpanded, setHeadersExpanded] = useState(false);
  const [quotedExpanded, setQuotedExpanded] = useState(false);

  useEffect(() => {
    const node = bodyRef.current;
    if (!node) {
      return;
    }
    node.focus();
    node.setSelectionRange(0, 0);
  }, []);

  const recipientPillText = useMemo(
    () => buildRecipientPillText(sourceMessage, mode),
    [sourceMessage, mode]
  );

  const canSend = useMemo(() => {
    const hasContent =
      draft.authoredBody.trim().length > 0 ||
      draft.quotedBody.trim().length > 0 ||
      draft.cc.trim().length > 0 ||
      draft.bcc.trim().length > 0 ||
      draft.subject.trim().length > 0 ||
      attachments.length > 0;
    if (!hasContent) {
      return false;
    }
    if (mode === 'forward') {
      return draft.to.trim().length > 0;
    }
    return true;
  }, [draft.authoredBody, draft.quotedBody, draft.cc, draft.bcc, draft.subject, draft.to, mode, attachments.length]);

  const handleFilesPicked = (event: React.ChangeEvent<HTMLInputElement>) => {
    const picked = event.target.files;
    if (!picked) return;
    const incoming = Array.from(picked);
    const currentTotal = attachments.reduce((acc, file) => acc + file.size, 0);
    const incomingTotal = incoming.reduce((acc, file) => acc + file.size, 0);
    const oversizeFile = incoming.find((file) => file.size > ATTACHMENT_TOTAL_BYTES_LIMIT);
    if (oversizeFile) {
      toast.error(`${oversizeFile.name} exceeds the 25 MB attachment limit.`);
    } else if (currentTotal + incomingTotal > ATTACHMENT_TOTAL_BYTES_LIMIT) {
      toast.error('Total attachments exceed the 25 MB limit.');
    } else {
      onAttachmentsChange([...attachments, ...incoming]);
    }
    event.target.value = '';
  };

  const handleRootKeyDown = (event: React.KeyboardEvent<HTMLElement>) => {
    if (event.key === 'Escape') {
      event.preventDefault();
      onCancel();
      return;
    }
    if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
      event.preventDefault();
      if (!isSending && canSend) {
        onSend();
      }
    }
  };

  return (
    <TooltipProvider delayDuration={400}>
      <section
        role="region"
        aria-label="Reply composer"
        onKeyDown={handleRootKeyDown}
        className="rounded-md border border-border bg-card shadow-sm"
      >
        <div className="flex items-center justify-between gap-2 border-b border-border/50 px-3 py-2">
          <div className="flex min-w-0 items-center gap-1">
            <button
              type="button"
              onClick={() => setHeadersExpanded((value) => !value)}
              aria-expanded={headersExpanded}
              aria-label="Toggle recipients"
              className="inline-flex max-w-full items-center gap-1 rounded-full bg-muted/60 px-3 py-1 text-xs hover:bg-muted"
            >
              <span className="truncate">{recipientPillText}</span>
              <ChevronDown className="h-3 w-3 shrink-0 opacity-70" />
            </button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label={`Reply mode: ${MODE_LABEL[mode]}`}
                  className="rounded p-1 text-muted-foreground hover:bg-muted/40 hover:text-foreground"
                >
                  <ChevronDown className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onSelect={() => onModeChange('reply')}>Reply</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => onModeChange('replyAll')}>Reply all</DropdownMenuItem>
                <DropdownMenuItem onSelect={() => onModeChange('forward')}>Forward</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <button
            type="button"
            onClick={onCancel}
            aria-label="Discard reply"
            className="rounded p-1 text-muted-foreground hover:bg-muted/40 hover:text-foreground"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {headersExpanded ? (
          <div className="space-y-2 border-b border-border/50 px-3 py-2">
            <Input
              value={draft.to}
              onChange={(event) => onDraftChange({ ...draft, to: event.target.value })}
              placeholder="To"
              aria-label="To"
              disabled={isSending}
            />
            <Input
              value={draft.cc}
              onChange={(event) => onDraftChange({ ...draft, cc: event.target.value })}
              placeholder="Cc"
              aria-label="Cc"
              disabled={isSending}
            />
            <Input
              value={draft.bcc}
              onChange={(event) => onDraftChange({ ...draft, bcc: event.target.value })}
              placeholder="Bcc"
              aria-label="Bcc"
              disabled={isSending}
            />
            <Input
              value={draft.subject}
              onChange={(event) => onDraftChange({ ...draft, subject: event.target.value })}
              placeholder="Subject"
              aria-label="Subject"
              disabled={isSending}
            />
          </div>
        ) : null}

        <MessageDraftRevisionNotice revision={revision} />
        <div className="space-y-2 px-3 py-2">
          <Textarea
            ref={bodyRef}
            value={draft.authoredBody}
            onChange={(event) => onDraftChange({ ...draft, authoredBody: event.target.value })}
            rows={4}
            aria-label="Reply body"
            disabled={isSending}
            className="resize-none border-0 px-0 shadow-none focus-visible:ring-0"
          />
          {draft.quotedBody.length > 0 ? (
            quotedExpanded ? (
              <Textarea
                value={draft.quotedBody}
                onChange={(event) => onDraftChange({ ...draft, quotedBody: event.target.value })}
                rows={8}
                aria-label="Quoted history"
                disabled={isSending}
                className="resize-none border-0 px-0 text-muted-foreground shadow-none focus-visible:ring-0"
              />
            ) : (
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={() => setQuotedExpanded(true)}
                    aria-label="Show trimmed content"
                    className="inline-flex h-6 min-w-7 items-center justify-center rounded-md border border-border/70 bg-muted/60 px-2 text-muted-foreground shadow-sm hover:bg-muted hover:text-foreground"
                  >
                    <Ellipsis className="h-3 w-3" strokeWidth={2} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="bottom">Show trimmed content</TooltipContent>
              </Tooltip>
            )
          ) : null}
        </div>

        {attachments.length > 0 ? (
          <div className="space-y-2 px-3 pb-2">
            <ul className="divide-y rounded-lg border" data-testid="composer-attachments-list">
              {attachments.map((file, index) => (
                <li
                  key={`${file.name}-${index}`}
                  className="flex items-center gap-3 px-3 py-2 text-sm"
                >
                  <Paperclip
                    className="h-4 w-4 shrink-0 text-muted-foreground"
                    aria-hidden
                  />
                  <span className="min-w-0 flex-1 truncate">{file.name}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {formatBytes(file.size)}
                  </span>
                  <button
                    type="button"
                    aria-label={`Remove ${file.name}`}
                    onClick={() =>
                      onAttachmentsChange(attachments.filter((_, i) => i !== index))
                    }
                    disabled={isSending}
                    className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted/40 hover:text-foreground disabled:opacity-60"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={handleFilesPicked}
          className="hidden"
        />

        <div className="flex items-center gap-2 border-t border-border/50 px-3 py-2">
          <div className="flex items-center">
            <Button
              type="button"
              onClick={onSend}
              disabled={isSending || !canSend}
              className="rounded-r-none"
            >
              {isSending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              Send
            </Button>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  disabled={isSending || !canSend}
                  aria-label="Send options"
                  className="rounded-l-none border-l border-primary-foreground/20 px-2"
                >
                  <ChevronDown className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem disabled>Schedule send</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="flex items-center gap-1 text-muted-foreground">
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isSending}
                  aria-label="Attach files"
                  className="rounded p-1.5 hover:bg-muted/40 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <Paperclip className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent>Attach files</TooltipContent>
            </Tooltip>
            {PRIMARY_FOOTER_ICONS.map(({ Icon, label }) => (
              <Tooltip key={label}>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    disabled
                    aria-label={label}
                    className="rounded p-1.5 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <Icon className="h-4 w-4" />
                  </button>
                </TooltipTrigger>
                <TooltipContent>Coming soon</TooltipContent>
              </Tooltip>
            ))}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label="More options"
                  className="rounded p-1.5 hover:bg-muted/40 hover:text-foreground"
                >
                  <MoreVertical className="h-4 w-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem disabled>Toggle formatting</DropdownMenuItem>
                <DropdownMenuItem disabled>Confidential mode</DropdownMenuItem>
                <DropdownMenuItem disabled>Insert signature</DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={onCancel}
                disabled={isSending}
                aria-label="Discard reply"
                className="ml-auto rounded p-1.5 text-muted-foreground hover:bg-muted/40 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </TooltipTrigger>
            <TooltipContent>Discard reply</TooltipContent>
          </Tooltip>
        </div>
      </section>
    </TooltipProvider>
  );
}
